import React, { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from '@/components/ui/sheet';
import { Boxes, SlidersHorizontal } from 'lucide-react';
import LeftRail from './LeftRail';
import RightInspector from './RightInspector';
import { useStudio, TEMPLATES } from '@/lib/store';

// Below the `lg` breakpoint the LeftRail / RightInspector are hidden. This
// component adds a bottom-anchored two-button bar that opens each rail in a
// slide-in Sheet, and shows the current template + panel count at a glance.
export default function MobileControls() {
  const [tplOpen, setTplOpen] = useState(false);
  const [inspOpen, setInspOpen] = useState(false);
  const templateId = useStudio((s) => s.templateId);
  const faces = useStudio((s) => s.faces);
  const currentTemplate = TEMPLATES.find((t) => t.id === templateId);

  // Auto-close the template sheet whenever the user picks a new template.
  const firstTemplateRender = useRef(true);
  useEffect(() => {
    if (firstTemplateRender.current) {
      firstTemplateRender.current = false;
      return;
    }
    setTplOpen(false);
  }, [templateId]);

  return (
    <div
      className="lg:hidden fixed left-0 right-0 bottom-0 z-40 border-t border-slate-200 bg-white/90 backdrop-blur-xl px-3 py-2 flex items-center gap-2"
      data-testid="mobile-controls"
      style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))' }}
    >
      <Sheet open={tplOpen} onOpenChange={setTplOpen}>
        <SheetTrigger asChild>
          <button
            className="flex-1 flex items-center gap-2 h-11 rounded-2xl bg-slate-900 text-white px-3"
            data-testid="btn-mobile-templates"
          >
            <Boxes className="w-4 h-4 shrink-0" />
            <div className="flex flex-col items-start min-w-0 leading-tight">
              <span className="text-[10px] uppercase tracking-widest text-white/60">Template</span>
              <span className="text-sm font-medium truncate">
                {currentTemplate?.name || 'Custom'} · {faces.length}
              </span>
            </div>
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0 w-[85%] sm:max-w-md overflow-hidden" data-testid="mobile-template-sheet">
          <SheetHeader className="px-4 py-3 border-b border-slate-200 flex-row items-center justify-between">
            <SheetTitle className="text-base">Templates</SheetTitle>
          </SheetHeader>
          <div className="overflow-y-auto max-h-[calc(100vh-3.5rem)]">
            <LeftRail />
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={inspOpen} onOpenChange={setInspOpen}>
        <SheetTrigger asChild>
          <button
            className="flex-1 flex items-center gap-2 h-11 rounded-2xl border border-slate-200 bg-white text-slate-900 px-3"
            data-testid="btn-mobile-inspector"
          >
            <SlidersHorizontal className="w-4 h-4 shrink-0" />
            <div className="flex flex-col items-start min-w-0 leading-tight">
              <span className="text-[10px] uppercase tracking-widest text-slate-400">Design</span>
              <span className="text-sm font-medium">Surface · Colors · Symmetry</span>
            </div>
          </button>
        </SheetTrigger>
        <SheetContent side="right" className="p-0 w-[92%] sm:max-w-md overflow-hidden" data-testid="mobile-inspector-sheet">
          <SheetHeader className="px-4 py-3 border-b border-slate-200 flex-row items-center justify-between">
            <SheetTitle className="text-base">Design controls</SheetTitle>
          </SheetHeader>
          <div className="overflow-y-auto max-h-[calc(100vh-3.5rem)]">
            <RightInspector />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
